import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
  signal,
  ViewChild,
  AfterViewInit,
  ChangeDetectorRef,
} from '@angular/core';
import { BusinessService } from '../../../services/business';
import { Client } from '../../../shared/interfaces';
import { ToastService } from '../../../shared/services';
import { PeopleAdd } from '../../../features/people/people-add/people-add';
import { BaseAuthComponent } from '../../common/BaseAuthComponent.class';
import { switchMap } from 'rxjs/operators';
import { of } from 'rxjs';

@Component({
  selector: 'app-people-add-drawer',
  standalone: true,
  imports: [CommonModule, PeopleAdd],
  templateUrl: './people-add-drawer.html',
  styleUrl: './people-add-drawer.scss',
})
export class PeopleAddDrawer
  extends BaseAuthComponent
  implements OnChanges, AfterViewInit
{
  @Input() open = false;
  @Input() endpoint: 'clients' | 'professionals' = 'clients';
  @Input() title: string = 'Nuevo Cliente';
  @Input() entityId: string | null = null; // For editing mode
  @Output() close = new EventEmitter<void>();
  @Output() created = new EventEmitter<Client>();
  @Output() updated = new EventEmitter<Client>();

  @ViewChild('peopleAdd') peopleAddComponent?: PeopleAdd;

  private businessService = inject(BusinessService);
  private toastService = inject(ToastService);
  private cdr = inject(ChangeDetectorRef);

  isLoading = signal(false);
  private wasOpen = false;
  private isInitializing = false;
  private shouldResetOnOpen = true; // Flag to control when to reset form on open

  constructor() {
    super();
  }

  ngAfterViewInit(): void {
    // Ensure ViewChild is available
    this.cdr.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Only handle when open changes
    if (changes['open']) {
      const currentValue = changes['open'].currentValue;
      const previousValue = changes['open'].previousValue;

      // Opening: from false to true
      if (currentValue === true && previousValue === false) {
        this.wasOpen = true;
        this.isInitializing = true;
        // Only reset form when drawer opens if flag is set (fresh open, not after error)
        // This prevents resetting form data when drawer stays open after an error
        if (!this.entityId && this.shouldResetOnOpen) {
          // Use a longer timeout to ensure ViewChild is available after view init
          setTimeout(() => {
            if (this.peopleAddComponent?.peopleForm) {
              this.peopleAddComponent.peopleForm.reset();
            }
            this.isInitializing = false;
          }, 300);
        } else {
          this.isInitializing = false;
        }
      }

      // Closing: from true to false
      if (currentValue === false && previousValue === true) {
        this.wasOpen = false;
        this.isLoading.set(false);
        // Reset form after animation completes (only when actually closing)
        setTimeout(() => {
          if (this.peopleAddComponent?.peopleForm) {
            this.peopleAddComponent.peopleForm.reset();
          }
        }, 400);
        // Reset flag when drawer closes successfully (ready for next fresh open)
        this.shouldResetOnOpen = true;
      }
    }
  }

  closeDrawer(): void {
    // Prevent closing during initialization
    if (this.isInitializing) {
      return;
    }
    // Always allow closing, but reset loading state
    this.isLoading.set(false);
    this.close.emit();
  }

  onCreate(): void {
    if (!this.peopleAddComponent) {
      return;
    }

    if (!this.peopleAddComponent.isValid()) {
      return;
    }

    // The photo goes in its own upload, never inside the JSON body
    const { avatarFile: _avatar, ...payload } = this.peopleAddComponent.getValue();
    const avatarFile = this.endpoint === 'professionals'
      ? this.peopleAddComponent.getProfessionalAvatarFile()
      : null;
    this.isLoading.set(true);

    const isEdit = !!this.entityId;
    const entityName =
      this.endpoint === 'clients' ? 'cliente' : 'profesional';

    if (isEdit) {
      // Update existing entity
      this.businessService
        .updateRecordByBusinessId<Client>(
          this.businessId,
          this.entityId!,
          payload,
          this.endpoint
        )
        .pipe(
          switchMap((res: any) => {
            // Upload avatar if provided and editing a professional
            if (this.endpoint === 'professionals' && avatarFile && res) {
              const professionalId = this.entityId!;
              return this.businessService.uploadProfessionalAvatar(
                this.businessId,
                professionalId,
                avatarFile
              ).pipe(
                switchMap(() => of(res))
              );
            }
            return of(res);
          })
        )
        .subscribe({
          next: (res: any) => {
            this.isLoading.set(false);
            if (res) {
              this.toastService.show(
                `${entityName.charAt(0).toUpperCase() + entityName.slice(1)} actualizado correctamente`,
                'success'
              );
              // Close drawer first
              this.closeDrawer();
              // Emit event after drawer starts closing
              setTimeout(() => {
                this.updated.emit(res as Client);
              }, 50);
            }
          },
          error: (err: any) => {
            this.isLoading.set(false);
            console.error('Error updating client:', err);

            const errorMessage = this.extractErrorMessage(err, entityName);

            this.toastService.show(errorMessage, 'warning', 6000);
            // Don't close drawer on error - keep form data
            // Prevent form reset on next open if drawer stays open
            this.shouldResetOnOpen = false;
          },
        });
    } else {
      // Create new entity
      this.businessService
        .createRecordByBusinessId<Client>(this.businessId, payload, this.endpoint)
        .pipe(
          switchMap((res: any) => {
            // Upload avatar if provided and creating a professional
            if (this.endpoint === 'professionals' && avatarFile && res) {
              const professionalId = (res as any).userId || (res as any).id;
              if (professionalId) {
                return this.businessService.uploadProfessionalAvatar(
                  this.businessId,
                  professionalId,
                  avatarFile
                ).pipe(
                  switchMap(() => of(res))
                );
              }
            }
            return of(res);
          })
        )
        .subscribe({
          next: (res) => {
            this.isLoading.set(false);
            if (res) {
              this.toastService.show(
                `${entityName.charAt(0).toUpperCase() + entityName.slice(1)} creado correctamente`,
                'success'
              );
              // Close drawer first
              this.closeDrawer();
              // Emit event after drawer starts closing
              setTimeout(() => {
                this.created.emit(res as Client);
              }, 50);
            }
          },
          error: (err: any) => {
            this.isLoading.set(false);
            console.error('Error creating client:', err);

            const errorMessage = this.extractErrorMessage(err, entityName);

            this.toastService.show(errorMessage, 'warning', 6000);
            // Don't close drawer on error - keep form data so user can correct and retry
            // Prevent form reset on next open if drawer stays open
            this.shouldResetOnOpen = false;
          },
        });
    }
  }

  private extractErrorMessage(err: any, entityName: string): string {
    const errorResponse = err?.error as {
      code?: string;
      message?: string;
      errors?: string[];
      details?: string;
    };

    // Prefer explicit errors array
    if (errorResponse?.errors && Array.isArray(errorResponse.errors) && errorResponse.errors.length > 0) {
      const relevantError =
        errorResponse.errors.find((e) => !e.toLowerCase().includes('error interno del servidor')) ||
        errorResponse.errors[0];
      return this.cleanValidationMessage(relevantError);
    }

    if (errorResponse?.message) {
      return this.cleanValidationMessage(errorResponse.message);
    }

    if (errorResponse?.details) {
      return this.cleanValidationMessage(errorResponse.details);
    }

    if (err?.message) {
      return this.cleanValidationMessage(err.message);
    }

    return `Error con el ${entityName}. Por favor verifica los datos e intenta nuevamente.`;
  }

  private cleanValidationMessage(message: string): string {
    if (!message) return 'Ocurrió un error, por favor intenta nuevamente.';

    // Remove generic prefix if present
    const parts = message.split('\n').map((p) => p.trim()).filter(Boolean);
    const candidate = parts.length > 1 ? parts[parts.length - 1] : parts[0];

    // Strip "Validation failed:" and severity markers
    const withoutValidation = candidate.replace(/^Validation failed:\s*/i, '');
    const withoutSeverity = withoutValidation.replace(/Severity:\s*Error/i, '').trim();

    // If message contains "Email: ..." or similar, return the portion after the last colon
    const colonIndex = withoutSeverity.lastIndexOf(':');
    if (colonIndex !== -1 && colonIndex + 1 < withoutSeverity.length) {
      return withoutSeverity.slice(colonIndex + 1).trim();
    }

    return withoutSeverity || message;
  }
}
